import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { HERO_BG } from "@/data/gameData";
import { Loader2, Sparkles } from "lucide-react";

export default function Auth() {
  const { login, register, loginGoogle, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const dest = location.state?.from || "/";
  useEffect(() => {
    if (user) navigate(dest, { replace: true });
  }, [user, dest, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-5 py-10">
      <div className="absolute inset-0">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-[#05050A]/80" />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-[#F43F5E] mb-3">
            <Sparkles className="w-4 h-4" />
            <span className="text-xs uppercase tracking-[0.3em] font-semibold">Uma escolha muda tudo</span>
          </div>
          <h1 className="font-display font-black text-4xl tracking-tighter leading-none">
            E SE FOSSE<br /><span className="text-[#F43F5E] text-glow">COMIGO?</span>
          </h1>
        </div>

        <div className="bg-[#0F0F14] border border-white/10 rounded-3xl p-7">
          <div className="flex gap-1 p-1 bg-white/5 rounded-full mb-6">
            {["login", "register"].map((m) => (
              <button key={m} data-testid={`tab-${m}`} onClick={() => { setMode(m); setError(""); }}
                className={`flex-1 py-2.5 rounded-full text-sm font-semibold transition-colors ${mode === m ? "bg-[#F43F5E] text-white" : "text-slate-400 hover:text-white"}`}>
                {m === "login" ? "Entrar" : "Criar conta"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <input data-testid="auth-name" required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Seu nome" className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3.5 outline-none focus:border-[#F43F5E]/60 transition-colors" />
            )}
            <input data-testid="auth-email" required type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="E-mail" className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3.5 outline-none focus:border-[#F43F5E]/60 transition-colors" />
            <input data-testid="auth-password" required type="password" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Senha (mín. 6)" className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3.5 outline-none focus:border-[#F43F5E]/60 transition-colors" />

            {error && <p data-testid="auth-error" className="text-sm text-[#F43F5E]">{error}</p>}

            <button data-testid="auth-submit" disabled={loading}
              className="w-full bg-[#F43F5E] hover:bg-[#E11D48] active:scale-95 transition-all rounded-2xl py-3.5 font-semibold shadow-lg shadow-[#F43F5E]/30 flex items-center justify-center gap-2 disabled:opacity-60">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === "login" ? "Entrar e jogar" : "Começar a jogar"}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <span className="h-px flex-1 bg-white/10" />
            <span className="text-xs text-slate-500">ou</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <button type="button" data-testid="google-login-btn" onClick={loginGoogle}
            className="w-full bg-white text-slate-900 hover:bg-slate-100 active:scale-95 transition-all rounded-2xl py-3.5 font-semibold flex items-center justify-center gap-3">
            <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.9 1.5l2.6-2.5C17.1 2.9 14.8 2 12 2 6.9 2 2.8 6.1 2.8 11.2S6.9 20.4 12 20.4c5.9 0 9.8-4.1 9.8-9.9 0-.7-.1-1.2-.2-1.7H12z" />
            </svg>
            Continuar com Google
          </button>
        </div>
        <p className="text-center text-xs text-slate-500 mt-5">Ficção para entretenimento. Não é aconselhamento financeiro, médico ou jurídico.</p>
      </motion.div>
    </div>
  );
}
